import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Tab,
  Nav,
  Form,
  Button,
  Card
} from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {toAbsoluteUrl} from 'src/marketConnect/helpers'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {fetchUserAccountDetailsAsync} from 'src/redux/actions/accountActions'
import {fetchPlatformsAsync} from 'src/redux/actions/platformActions'
import {handleOauthForConnectPlatformAsync, loginAsync, registrationAsync} from 'src/redux/actions/authActions'
import {accessSessionTokenKeyName, refreshSessionTokenKeyName} from 'src/utils/constants'
import {finalizeTikTokAfterAuth} from 'src/utils/finalizeTikTokAfterAuth'
import {resetImpersonationForFreshLogin} from 'src/utils/impersonationTokens'

import {
  captureWalmartMarketplaceHandoffFromSearch,
  getWalmartPlatformIdFromPayload,
  getUserIdFromAuthPayload,
  redirectToWalmartMarketplaceAuthIfNeeded,
} from 'src/utils/walmartMarketplaceHandoff'
import useAppToast from 'src/hooks/useAppToast'
import {messageTemplete} from 'src/utils/messageTemplete'
import {userInfo} from 'os'
import { TwakScript } from 'src/externalScript/TwakScript'
import { setLoggingData } from 'src/redux/slices/authSlice';
import ReCAPTCHA from 'react-google-recaptcha';

// For Login
  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Minimum 8 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Password is required'),
  })

  const initialValues = {
    email: '',
    password: '',
  }

  // For Sign Up
  const initialValuesForSignUp = {
    name: '',
    email: '',
    password: '',
    changepassword: '',
    acceptTerms: true,
  }

  const registrationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Full name is required'),
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Email is required'),
  
    password: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[a-zA-Z\d@$!%*?&]{8,}$/,
      'Use 8 or more characters with at least one uppercase letter, one lowercase letter, one special character, and one number'
    )
      .max(50, 'Maximum 50 symbols')
      .required('Password is required'),
    changepassword: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[a-zA-Z\d@$!%*?&]{8,}$/,
      'Use 8 or more characters with at least one uppercase letter, one lowercase letter, one special character, and one number'
    )
      .max(50, 'Maximum 50 symbols')
      .required('Password confirmation is required')
      .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
  })

const LoginRegister = () => {
  const [key, setKey] = useState<any>('login');

  //For Login
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const dispatch = useAppDispatch()
  const {} = useAppSelector((state) => state.auth)
  const {successToast, errorToast, warningToast} = useAppToast()
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        await dispatch(loginAsync({email: values.email, password: values.password}))
          .then(async (response) => {

            if(response.payload?.status=='401'){
              warningToast(response?.payload?.data?.detail)
            }

            if (response?.payload?.status === 401) {
              errorToast(response?.payload?.detail)
              setLoading(false)
            } else if (response?.payload?.access) {
              resetImpersonationForFreshLogin()
              localStorage.setItem(accessSessionTokenKeyName, response?.payload?.access)
              localStorage.setItem(refreshSessionTokenKeyName, response?.payload?.refresh)
              dispatch(
                setLoggingData({
                  isLoggedIn: true,
                  userData: response.payload.data,
                  status: response.payload.status,
                })
              )
              let walmartOauthUserId = getUserIdFromAuthPayload(response?.payload)
              if (!walmartOauthUserId) {
                const userResponse = await dispatch(fetchUserAccountDetailsAsync())
                walmartOauthUserId = getUserIdFromAuthPayload(userResponse?.payload)
              }
              const walmartPlatformResponse = await dispatch(fetchPlatformsAsync({search: 'WALMART'}))
              const walmartPlatformId = getWalmartPlatformIdFromPayload(walmartPlatformResponse?.payload)
              if (await redirectToWalmartMarketplaceAuthIfNeeded((params) =>
                dispatch(handleOauthForConnectPlatformAsync(params)),
                {user_id: walmartOauthUserId, platform_id: walmartPlatformId}
              )) {
                return
              }

              await finalizeTikTokAfterAuth(dispatch)

              successToast(messageTemplete.LOGIN_SUCCESS)
              setLoading(false)
            } else {
              errorToast(messageTemplete.SOMETHING_WENT_WRONG)
              setLoading(false)
            }
          })
          .catch((error) => {
            setLoading(false)
          })
      } catch (error) {
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  //For SignUp
  const [disableForCaptcha, setDisableForCaptcha] = useState(true)
  const navigate = useNavigate()
  const {} = useAppSelector((state) => state)
  const { search } = useLocation(); // Hook to access the URL query parameters
  const queryParams = new URLSearchParams(search);
  const nameFromURL = queryParams.get('name');
  const emailFromURL = queryParams.get('email');
  const invitedByFromURL = queryParams.get('invited_by');
  const source = queryParams.get('SOURCE') || queryParams.get('source')
  const sourcesm = queryParams.get('source')

  useEffect(() => {
    const handoffStatus = captureWalmartMarketplaceHandoffFromSearch(search)
    if (handoffStatus === 'invalid') {
      errorToast('Walmart authorization URL is missing or invalid.')
    }
  }, [errorToast, search])

  const initialValuesForSignUp = {
    name: nameFromURL || '',
    email: emailFromURL || '',
    password: '',
    changepassword: '',
    acceptTerms: true,
    invited_by:invitedByFromURL||''
  };

  const registrationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Full name is required'),
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Email is required'),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[a-zA-Z\d!@#$%^&*(),.?":{}|<>]{8,}$/,
        'Use 8 or more characters with at least one uppercase letter, one lowercase letter, one special character, and one number'
      )
      .max(50, 'Maximum 50 symbols')
      .required('Password is required'),
    changepassword: Yup.string()
      .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match")
      .required('Password confirmation is required'),
    acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
  })

  const formikRegister = useFormik({
      initialValues : initialValuesForSignUp,
      validationSchema: registrationSchema,
      onSubmit: async (values, { setStatus, setSubmitting }) => {
        setLoading(true)
        try {
          if(invitedByFromURL){
            const response = await dispatch(
              registrationAsync({
                name: nameFromURL,
                email: emailFromURL,
                password: values.password,
                user_type: 'USER',
                is_superuser: false,
                is_staff: false,
                is_active: true,
                invited_by:  Number(invitedByFromURL),
                source: source ? source : null
              })
            )
            if (response?.payload?.status_code === 201) {
              successToast(response.payload.massage)
              setLoading(false)
              setTimeout(() => {
                dispatch(
                  loginAsync({
                    email: values.email,
                    password: values.password,
                  })
                ).then(async (loginResponse) => {
                  if (loginResponse.payload?.access) {
                    localStorage.setItem(accessSessionTokenKeyName, loginResponse?.payload?.access)
                    localStorage.setItem(refreshSessionTokenKeyName, loginResponse?.payload?.refresh)
                let walmartOauthUserId = getUserIdFromAuthPayload(loginResponse?.payload)
                if (!walmartOauthUserId) {
                  const userResponse = await dispatch(fetchUserAccountDetailsAsync())
                  walmartOauthUserId = getUserIdFromAuthPayload(userResponse?.payload)
                }
                  const walmartPlatformResponse = await dispatch(fetchPlatformsAsync({search: 'WALMART'}))
                  const walmartPlatformId = getWalmartPlatformIdFromPayload(walmartPlatformResponse?.payload)
                  if (await redirectToWalmartMarketplaceAuthIfNeeded((params) =>
                    dispatch(handleOauthForConnectPlatformAsync(params)),
                    {user_id: walmartOauthUserId, platform_id: walmartPlatformId}
                  )) {
                    return
                  }
                    dispatch(
                      setLoggingData({
                        isLoggedIn: true,
                        userData: loginResponse.payload.data,
                        status: loginResponse.payload.status,
                      })
                    )
                  await finalizeTikTokAfterAuth(dispatch)
                    
                    navigate('/')
                  } else {
                    errorToast('Login failed')
                  }
                })
              }, 1500)
            } else if (response?.payload?.data?.status_code === 400) {
              errorToast(response.payload.data.massage)
              setLoading(false)
            }
  
          }else{
            const response = await dispatch(
              registrationAsync({
                name: values.name,
                email: values.email,
                password: values.password,
                user_type: 'USER',
                is_superuser: false,
                is_staff: false,
                is_active: true,
                invited_by:null,
                source: source ? source : null
                // parent: invitedByFromURL ? Number(invitedByFromURL) : null,
              })
            )
            if (response?.payload?.status_code === 201) {
              successToast(response.payload.massage)
              setLoading(false)
              setTimeout(() => {
                dispatch(
                  loginAsync({
                    email: values.email,
                    password: values.password,
                  })
                ).then(async (loginResponse) => {
                  if (loginResponse.payload?.access) {
                    localStorage.setItem(accessSessionTokenKeyName, loginResponse?.payload?.access)
                    localStorage.setItem(refreshSessionTokenKeyName, loginResponse?.payload?.refresh)
                  let walmartOauthUserId = getUserIdFromAuthPayload(loginResponse?.payload)
                  if (!walmartOauthUserId) {
                    const userResponse = await dispatch(fetchUserAccountDetailsAsync())
                    walmartOauthUserId = getUserIdFromAuthPayload(userResponse?.payload)
                  }
                    const walmartPlatformResponse = await dispatch(fetchPlatformsAsync({search: 'WALMART'}))
                    const walmartPlatformId = getWalmartPlatformIdFromPayload(walmartPlatformResponse?.payload)
                    if (await redirectToWalmartMarketplaceAuthIfNeeded((params) =>
                      dispatch(handleOauthForConnectPlatformAsync(params)),
                      {user_id: walmartOauthUserId, platform_id: walmartPlatformId}
                    )) {
                      return
                    }
                    dispatch(
                      setLoggingData({
                        isLoggedIn: true,
                        userData: loginResponse.payload.data,
                        status: loginResponse.payload.status,
                      })
                    )
                  await finalizeTikTokAfterAuth(dispatch)
                    const isWooCommerceSource =
                      (source && ['WOOCOMMERCE', 'woocommerce'].includes(source)) ||
                      (sourcesm && ['WOOCOMMERCE', 'woocommerce'].includes(sourcesm))

                    if (isWooCommerceSource) {
                      navigate('/pricing')
                    } else {
                      navigate('/')
                    }
                  } else {
                    errorToast('Login failed')
                  }
                })
              }, 1500)
            } else if (response?.payload?.status === 400) {
              errorToast(response.payload.data.massage)
              setLoading(false)
            }
          }
          
  
      
        } catch (error) {
          setLoading(false)
        }
      },
    })


    const getPasswordStrengthLevel = (password) => {
      let score = 0
      if (!password) return 0
      if (password.length >= 8) score++
      if (/[A-Z]/.test(password)) score++
      if (/[a-z]/.test(password)) score++
      if (/\d/.test(password)) score++
      if (/[@$!%*?&]/.test(password)) score++
      return Math.min(score, 4)
    }

  return (
    <>
    <Container className="py-5 d-flex justify-content-center align-items-center">
      <style>
        {`
          .floating-label-group {
            position: relative;
            margin-bottom: 1.5rem;
          }

          .floating-label-group input {
            border: 1px solid #ced4da;
            border-radius: 0.5rem;
            padding: 1rem 0.75rem 1rem 0.75rem;
            font-size: 1rem;
            width: 100%;
            transition: border-color 0.3s, padding-top 0.3s;
            background: none;
          }

          .floating-label-group label {
            position: absolute;
            top: 1rem;
            left: 0.75rem;
            font-size: 1rem;
            color: #6c757d;
            transition: 0.2s ease all;
            pointer-events: none;

            /* To "cut" the label out of the border */
            background: white;
            padding: 0 0.5rem;
            transform-origin: left top;
          }

          .floating-label-group input:focus {
            outline: none;
            border-color: #0d6efd;
          }

          .floating-label-group input:focus + label,
          .floating-label-group input:not(:placeholder-shown) + label {
            top: -0.7rem;
            left: 0.6rem;
            font-size: 0.8rem;
            color: #0d6efd;
            padding: 0 0.25rem;
          }

          /* Google button and icon styles */

          .google-button {
            border: 1px solid #0d6efd;
            color: #0d6efd;
            transition: all 0.3s ease-in-out;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }

          .google-icon {
            color: #0d6efd;
            transition: color 0.3s ease-in-out;
          }

          .google-button:hover {
            background-color: #0d6efd;
            color: white;
            transform: scale(1.02);
          }

          .google-button:hover .google-icon {
            color: white;
          }

          .card-style {
            background-color: #ffffff;
            border-radius: 1rem;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
            width: 100%;
            max-width: 500px;
          }

          .nav-link {
            font-weight: 600;
            border-radius: 50px !important;
            padding: 0.5rem 1.5rem;
          }

          .nav-pills .nav-link.active {
            background-color: #0d6efd;
            color: white;
          }

          .form-check-label {
            font-size: 0.9rem;
          }

        `}
      </style>

      <Card className="p-4 card-style">
        <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>

          <Tab.Content>
            {/* Login */}
            <Tab.Pane eventKey="login">
              <div className="text-center mb-3">
                <Button
                  variant="outline-primary"
                  className="google-button d-flex align-items-center justify-content-center gap-2 w-100"
                  style={{ border: "1px solid #0d6efd" }}
                >
                  <img
                    src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png"
                    alt="Google logo"
                    width="20"
                    height="20"
                    className="google-icon"
                  />
                  Sign up with Google
                </Button>
                <div className="d-flex align-items-center mt-4 mb-2">
                  <div className="flex-grow-1 border-bottom"></div>
                  <span className="mx-2 text-muted fw-bold">Or</span>
                  <div className="flex-grow-1 border-bottom"></div>
                </div>
              </div>

              <Form onSubmit={formik.handleSubmit}>
                <div className="floating-label-group">
                  <Form.Control
                    placeholder=" "
                    {...formik.getFieldProps('email')}
                      className={clsx(
                        'form-control bg-transparent',
                        {'is-invalid': formik.touched.email && formik.errors.email},
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                      type='email'
                      name='email'
                      autoComplete='off'
                    required
                  />
                  <Form.Label>Email address</Form.Label>
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container ps-2'>
                      <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.email}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="floating-label-group">
                  <Form.Control
                    type="password"
                    placeholder=" "
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid': formik.touched.password && formik.errors.password,
                      },
                      {
                        'is-valid': formik.touched.password && !formik.errors.password,
                      }
                    )}
                    required
                  />
                  <Form.Label>Password</Form.Label>
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container ps-2'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.password}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-end mb-3">
                  <Link to="/auth/forgot-password-page" className="link-primary text-decoration-none">
                    Forgot Password?
                  </Link>
                </div>


                <Button variant="primary" type="submit" className="w-100 mb-3"
                disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!loading && <span className='indicator-label'>Sign In</span>}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </Button>

                <p className="text-center mb-0">
                  Not a member? <a href="#" onClick={() => setKey('register')}>Register</a>
                </p>
              </Form>
            </Tab.Pane>

            {/* Register */}
            <Tab.Pane eventKey="register">
              <div className="text-center mb-3">
                <Button
                  variant="outline-primary"
                  className="google-button d-flex align-items-center justify-content-center gap-2 w-100"
                  style={{ border: "1px solid #0d6efd" }}
                >
                  <img
                    src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png"
                    alt="Google logo"
                    width="20"
                    height="20"
                    className="google-icon"
                  />
                  Sign up with Google
                </Button>
                <div className="d-flex align-items-center mt-4 mb-2">
                  <div className="flex-grow-1 border-bottom"></div>
                  <span className="mx-2 text-muted fw-bold">Or</span>
                  <div className="flex-grow-1 border-bottom"></div>
                </div>
              </div>

              <Form onSubmit={formikRegister.handleSubmit}>
                <div className="floating-label-group">
                  <Form.Control
                    type="text"
                    placeholder=" "
                    required
                    autoComplete='off'
                    {...formikRegister.getFieldProps('name')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid': formikRegister.touched.name && formikRegister.errors.name,
                      },
                      {
                        'is-valid': formikRegister.touched.name && !formikRegister.errors.name,
                      }
                    )}
                  />
                  <Form.Label>Full Name</Form.Label>
                  {formikRegister.touched.name && formikRegister.errors.name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{String(formikRegister.errors.name)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* <div className="floating-label-group">
                  <Form.Control
                    type="text"
                    placeholder=" "
                    required
                  />
                  <Form.Label>Username</Form.Label>
                </div> */}

                <div className="floating-label-group">
                  <Form.Control
                    type="email"
                    placeholder=" "
                    autoComplete='off'
                    {...formikRegister.getFieldProps('email')}
                    className={clsx(
                      'form-control bg-transparent',
                      {'is-invalid': formikRegister.touched.email && formikRegister.errors.email},
                      {
                        'is-valid': formikRegister.touched.email && !formikRegister.errors.email,
                      }
                    )}
                    required
                  />
                  <Form.Label>Email</Form.Label>
                  {formikRegister.touched.email && formikRegister.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{String(formikRegister.errors.email)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="floating-label-group">
                  <Form.Control
                    type="password"
                    placeholder=" "
                    {...formikRegister.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid': formikRegister.touched.password && formikRegister.errors.password,
                      },
                      {
                        'is-valid': formikRegister.touched.password && !formikRegister.errors.password,
                      }
                    )}
                    required
                  />
                  <Form.Label>Password</Form.Label>

                  {formikRegister.touched.password && formikRegister.errors.password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formikRegister.errors.password}</span>
                      </div>
                    </div>
                  )}

                  {/* Password Strength Meter */}
                  <div className='d-flex align-items-center mb-3 mt-3'>
                    {[0, 1, 2, 3].map((i) => {
                      const strength = getPasswordStrengthLevel(formikRegister.values.password)
                      return (
                        <div
                          key={i}
                          className={`flex-grow-1 rounded h-5px me-2 ${i < strength ? 'bg-success' : 'bg-secondary'}`}
                          style={{ transition: 'background-color 0.3s ease' }}
                        />
                      )
                    })}
                  </div>

                  <div className='text-muted'>
                    Use 8 or more characters with a mix of letters, numbers & symbols.
                  </div>
                </div>


                <div className="floating-label-group">
                  <Form.Control
                    type="password"
                    placeholder=" "
                    {...formikRegister.getFieldProps('changepassword')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid': formikRegister.touched.changepassword && formikRegister.errors.changepassword,
                      },
                      {
                        'is-valid': formikRegister.touched.changepassword && !formikRegister.errors.changepassword,
                      }
                    )}
                    required
                  />
                  <Form.Label>Confirm Password</Form.Label>
                  {formikRegister.touched.changepassword && formikRegister.errors.changepassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formikRegister.errors.changepassword}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='d-flex align-items-center justify-content-center'>
                  <p style={{ fontSize: '12.5px' }}>By signing up, you agree to our <span className='text-primary fw-bold'><a href="https://constacloud.com/tou.html" target='_blank'>Terms and Conditions</a></span> and <span className='text-primary fw-bold'><a href="https://constacloud.com/privacy-policy.html" target='_blank'>Privacy Policy</a></span></p>
                </div>

                <ReCAPTCHA
                  // ref={(r) => this.recaptcha = r}
                  sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY!}
                  onChange={(val)=>{
                    setDisableForCaptcha(false)
                  }}
                />

                <Button variant="success" type="submit" className="w-100 mb-3"
                  disabled={formik.isSubmitting || !formik.isValid || disableForCaptcha}
                >
                  {!loading && <span className='indicator-label'>Submit</span>}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </Button>

                <p className="text-center mb-0">
                  Already have an account? <a href="#" onClick={() => setKey('login')}>Login</a>
                </p>
              </Form>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card>
    </Container>
    <TwakScript/>
    </>
  );
};

export default LoginRegister;
